import React, { useState, useContext, useCallback, useEffect, useRef } from "react";
import { NotificationManager } from "react-notifications";
import { useParams } from "react-router-dom";
import useApi from "../Api/Api";
import AdminDiv from "../AdminDiv";
import Input from "../Components/Input";
import SalesLayout from "./SalesLayout";
import EventLayout from "./EventLayout";
import { FilterContext } from "../FilterContextProvider";
import Button from "../Components/Button";
import SettlementCalculator from "./SettlementCalculator";
import useLocalState from "../hooks/useLocalState";
import ReactToPrint from "react-to-print";
import { UserContext } from "../Auth/UserContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import Switch from "react-switch";
import CleaveInput from "../Components/CleaveInput";
import storageManager from "../Utility/StorageManager";
import { kNumberOfUpdatesAllowed } from "./constants";
import OnlineWrapper from "./OnlineWrapper";
import OfflineWrapper from "./OfflineWrapper";

const EventSummary = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const printRef = useRef();
  const params = useParams();
  const [showButtons, setShowButtons] = useState(false);
  const [sales, setSales] = useState([]);
  const [recalculateAlert, setRecalculateAlert] = useState(false);
  const [dataCached, setDataCached] = useLocalState(`dataCached`, false);
  const { isAdmin } = useContext(UserContext);
  const { isManager } = useContext(UserContext);
  const [eventLastUpdatedBy, setEventLastUpdatedBy] = useState({});
  const [artist, setArtist] = useState({});
  const [hideButtons, setHideButtons] = useState(false);
  const [showNewerVersionWarning, setShowNewerVersionWarning] = useState(false);

  const eventFee = {
    amount: "",
    description: "",
    before_hall_fee: "",
    event_id: params.eventId,
  };

  const [eventFees, setEventFees] = useLocalState(`event/${params.eventId}/fees`, []);

  const [clothingSetup, setClothingSetup] = useLocalState(`event/${params.eventId}/clothingSetup`, {
    hall_rate: "",
    event_id: params.eventId,
    cc_fee: 0,
  });

  const [otherSetup, setOtherSetup] = useLocalState(`event/${params.eventId}/otherSetup`, {
    hall_rate: "",
    event_id: params.eventId,
    cc_fee: 0,
  });

  const [musicSetup, setMusicSetup] = useLocalState(`event/${params.eventId}/musicSetup`, {
    hall_rate: "",
    event_id: params.eventId,
    cc_fee: 0,
  });

  const [tempTaxPercent, setTempTaxPercent] = useLocalState(`event/${params.eventId}/tempTaxPercent`, "");

  const [tempCCPercent, setTempCCPercent] = useLocalState(`event/${params.eventId}/tempCCPercent`, "");

  const [event, setEvent] = useLocalState(`event/${params.eventId}/info`, {
    artist_id: filterData.artist_id,
    id: params.eventId,
    tax_rate: tempTaxPercent / 100,
    cc_rate: tempCCPercent / 100,
  });

  const editedEvent = {
    ...event,
    tax_rate: tempTaxPercent / 100,
    cc_rate: tempCCPercent / 100,
  };

  const showAristsAlert = () => {
    if (!event.cash_received && !event.check_due) {
      return false;
    }
    if (event.cash_received && event.check_due) {
      return (
        parseFloat(event.cash_paid_to_artist) + parseFloat(event.check_due_to_artist) !==
        parseFloat(event.total_from_venue)
      );
    }
    if (event.cash_received) {
      return parseFloat(event.cash_paid_to_artist) !== parseFloat(event.total_from_venue);
    }
    if (event.check_due) {
      return parseFloat(event.check_due_to_artist) !== parseFloat(event.total_from_venue);
    }
  };

  const showVenueAlert = () => {
    if (!event.cash_received_venue && !event.check_due_venue) {
      return false;
    }
    if (event.cash_received_venue && event.check_due_venue) {
      return (
        parseFloat(event.cash_paid_to_venue) + parseFloat(event.check_due_to_venue) !== parseFloat(event.total_to_venue)
      );
    }
    if (event.cash_received_venue) {
      return parseFloat(event.cash_paid_to_venue) !== parseFloat(event.total_to_venue);
    }
    if (event.check_due_venue) {
      return parseFloat(event.check_due_to_venue) !== parseFloat(event.total_to_venue);
    }
  };

  const changesMade = () => {
    setRecalculateAlert(true);
    setShowButtons(true);
  };

  const handleTaxedChange = (name, state, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    switch (name) {
      case "Clothing":
        return setClothingSetup({
          ...clothingSetup,
          taxed: state,
        });
      case "Other":
        return setOtherSetup({
          ...otherSetup,
          taxed: state,
        });
      case "Music":
        return setMusicSetup({
          ...musicSetup,
          taxed: state,
        });
      default:
    }
  };

  const handleHallRateChange = (name, e, offlineState) => {
    setDataCached(offlineState);
    switch (name) {
      case "Clothing":
        return setClothingSetup({
          ...clothingSetup,
          temp_decimal: e.target.value,
          hall_rate: e.target.value / 100,
        });
      case "Other":
        return setOtherSetup({
          ...otherSetup,
          temp_decimal: e.target.value,
          hall_rate: e.target.value / 100,
        });
      case "Music":
        return setMusicSetup({
          ...musicSetup,
          temp_decimal: e.target.value,
          hall_rate: e.target.value / 100,
        });
      default:
    }
  };

  const handleTaxRateOverrideChange = (name, e, offlineState) => {
    setDataCached(offlineState);
    switch (name) {
      case "Clothing":
        return setClothingSetup({
          ...clothingSetup,
          tax_rate_override_pct: e.target.value,
          tax_rate_override: e.target.value / 100,
        });
      case "Other":
        return setOtherSetup({
          ...otherSetup,
          tax_rate_override_pct: e.target.value,
          tax_rate_override: e.target.value / 100,
        });
      case "Music":
        return setMusicSetup({
          ...musicSetup,
          tax_rate_override_pct: e.target.value,
          tax_rate_override: e.target.value / 100,
        });
      default:
    }
  };

  const handleChange = (e, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    setEvent({
      ...event,
      [e.target.id]: e.target.value,
    });
    switch (e.target?.id) {
      case "attendance":
        return;
      case "cc_sales":
        return (
          event.vendor === "Artist" &&
          setEvent({
            ...event,
            [e.target.id]: e.target.value,
            cc_deposit: e.target.value,
          })
        );
      default:
    }
  };

  const handleTempCCPercent = (e, offlineState) => {
    setDataCached(offlineState);
    setRecalculateAlert(true);
    setShowButtons(true);
    setTempCCPercent(e.target.value);
  };

  const handleTempTaxPercent = (e, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    setTempTaxPercent(e.target.value);
  };

  const handleRadioChange = ({ target: { name, value } }, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    switch (name) {
      case "tax_kept_by_venue":
        return setEvent({
          ...event,
          [name]: !!value,
          remitted: false,
        });
      default:
        setEvent({
          ...event,
          [name]: !!value,
        });
    }
  };

  const handleCheckChange = ({ target: { id } }, state, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    if (state) {
      switch (id) {
        case "cash_received":
          return setEvent({
            ...event,
            [id]: state,
            cash_paid_to_artist: event.total_from_venue,
            check_due_to_artist: 0,
          });
        case "check_due":
          return setEvent({
            ...event,
            [id]: state,
            cash_paid_to_artist: 0,
            check_due_to_artist: event.total_from_venue,
          });
        case "cash_received_venue":
          return setEvent({
            ...event,
            [id]: state,
            cash_paid_to_venue: event.total_to_venue,
            check_due_to_venue: 0,
          });
        case "check_due_venue":
          return setEvent({
            ...event,
            [id]: state,
            cash_paid_to_venue: 0,
            check_due_to_venue: event.total_to_venue,
          });
        default:
          setEvent({
            ...event,
            [id]: state,
          });
      }
    } else {
      setEvent({
        ...event,
        [id]: state,
      });
    }
  };

  const handleCCProrateToggle = (state, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    setEvent({
      ...event,
      cc_fee_prorated: !state,
    });
  };

  const handleNewTaxFormulaToggle = (state, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    setEvent({
      ...event,
      new_tax_formula: !state,
    });
  };

  const handlePartySoldChange = ({ target: { value } }, offlineState) => {
    changesMade();
    switch (value) {
      case "Artist":
        return setEvent({
          ...event,
          cash_received: false,
          check_due: false,
          cash_paid_to_artist: 0,
          check_due_to_artist: 0,
          cc_deposit: event.cc_sales,
          vendor: value,
        });
      case "Venue":
        return setEvent({
          ...event,
          cash_received_venue: false,
          check_due_venue: false,
          cash_paid_to_venue: 0,
          check_due_to_venue: 0,
          cc_deposit: 0,
          vendor: value,
        });
      default:
    }
    setDataCached(offlineState);
    setShowButtons(true);
  };

  const handleFeeChange = (e, i, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    const newFees = eventFees.map((obj, j) => (j === i ? { ...obj, [e.target.id]: e.target.value, toAdd: true } : obj));
    setEventFees(newFees);
  };

  const handleFeeCheckChange = (state, i, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    const newFees = eventFees.map((obj, j) => (j === i ? { ...obj, before_hall_fee: state, toAdd: true } : obj));
    setEventFees(newFees);
  };

  const addFee = (e, offlineState) => {
    setDataCached(offlineState);
    setShowButtons(true);
    e.preventDefault();
    const newFee = { ...eventFee };
    setEventFees([...eventFees, newFee]);
  };

  const removeFee = (e, eventFee, i, offlineState) => {
    changesMade();
    setDataCached(offlineState);
    e.preventDefault();
    if (eventFee.id) {
      const newFees = eventFees.map((obj, j) => (j === i ? { ...obj, toRemove: true, toAdd: false } : obj));
      setEventFees(newFees);
    } else {
      const newFees = eventFees.map((obj, j) => (j === i ? { ...obj, toAdd: false } : obj));
      setEventFees(newFees);
    }
  };

  const handleHideButtons = () => {
    if (showButtons) {
      window.alert("You have pending changes, either revert or save them to print.");
    } else {
      setHideButtons(!hideButtons);
    }
  };

  const revert = () => {
    let check = "";
    if (!showButtons) {
      check = window.confirm("Are you sure you want to revert? All unsaved changes will be lost.");
    } else {
      check = true;
    }
    if (check) {
      setShowButtons(false);
      setEvent(JSON.parse(storageManager.getItem(`event/${params.eventId}/originalEvent`)));
      setEventFees(JSON.parse(storageManager.getItem(`event/${params.eventId}/originalFees`)));
      setTempTaxPercent(JSON.parse(storageManager.getItem(`event/${params.eventId}/originalEvent`)).tax_rate * 100);
      setTempCCPercent(JSON.parse(storageManager.getItem(`event/${params.eventId}/originalEvent`)).cc_rate * 100);
      setClothingSetup({
        ...JSON.parse(storageManager.getItem(`event/${params.eventId}/originalClothingSetup`)),
        temp_decimal:
          JSON.parse(storageManager.getItem(`event/${params.eventId}/originalClothingSetup`)).hall_rate * 100,
      });
      setOtherSetup({
        ...JSON.parse(storageManager.getItem(`event/${params.eventId}/originalOtherSetup`)),
        temp_decimal: JSON.parse(storageManager.getItem(`event/${params.eventId}/originalOtherSetup`)).hall_rate * 100,
      });
      setMusicSetup({
        ...JSON.parse(storageManager.getItem(`event/${params.eventId}/originalMusicSetup`)),
        temp_decimal: JSON.parse(storageManager.getItem(`event/${params.eventId}/originalMusicSetup`)).hall_rate * 100,
      });
      setShowNewerVersionWarning(false);
    }
  };

  const afterSubmit = () => {
    setShowNewerVersionWarning(false);
    getEvent();
    getEventFees();
    getEventCategorySetups();
  };

  let hallRatesToSave = [];

  const eventFeesToAdd = eventFees.filter((each) => each.toAdd === true);
  const eventFeesToRemove = eventFees.filter((each) => each.toRemove === true);

  const handleUpdateEvent = (finalize) => {
    let alerts = [
      showAristsAlert() && "Artists payment info does not match settlement info.",
      showVenueAlert() && "Venue payment info does not match settlement info.",
    ]
      .filter((item) => !!item)
      .join("\n");

    let check = "";
    if (finalize && (!isAdmin || alerts)) {
      check = window.confirm(
        `Are you sure everything looks right? You will NOT be able to submit again, only an admin can make further changes after submission.` +
          (alerts ? `\n\nWarnings:\n\n${alerts}` : "")
      );
    } else {
      check = true;
    }
    if (check) {
      Object.entries(editedEvent)
        .filter((each) => typeof each[1] === "string" && each[0] !== "name")
        .filter((each) => each[1].includes(","))
        .forEach((each) => {
          editedEvent[each[0]] = parseFloat(each[1].replace(/,/g, ""));
        });
      setIsFetching(true);
      hallRatesToSave.push(clothingSetup, otherSetup, musicSetup);
      let numberOfUpdates = event.number_of_updates;
      if (finalize) {
        numberOfUpdates += 1;
      }
      api
        .put(`/events/${event.id}`, {
          event: {
            ...editedEvent,
            number_of_updates: numberOfUpdates,
            event_category_setups_attributes: hallRatesToSave.map((each) => ({
              id: each.id,
              name: each.name,
              hall_rate: each.hall_rate,
              taxed: each.taxed,
              gross_sales: each.gross_sales,
              gross_tax: each.gross_tax,
              gross_hall: each.gross_hall,
              category_id: each.category_id,
              event_id: event.id,
              tax_rate_override: each.tax_rate_override || null,
            })),
            event_fees_attributes: [
              ...eventFeesToAdd.map((each) => ({
                id: each.id,
                amount: parseFloat(`${each.amount}`.replace(/,/g, "")),
                description: each.description,
                before_hall_fee: each.before_hall_fee,
              })),
              ...eventFeesToRemove.map((each) => ({
                id: each.id,
                _destroy: "1",
              })),
            ],
          },
        })
        .then((event) => {
          setEvent(event.data);
          afterSubmit();
          NotificationManager.success("Event successfully updated", "Success", 2000);
          setErrorMessage(false);
        })
        .catch((error) => {
          setErrorMessage(error.response.data);
          NotificationManager.error("Something went wrong. Event NOT updated", "Failure", 5000);
        })
        .finally(() => {
          setIsFetching(false);
        });
      setShowButtons(false);
    }
  };

  const handleFinalizeEvent = (e) => {
    e.preventDefault();
    if (!editedEvent.gross_sales && !window.confirm("Event has no gross sales. Are you sure you want to continue?")) {
      return;
    }
    handleUpdateEvent(true);
  };

  const handleSaveEvent = (e) => {
    e.preventDefault();
    handleUpdateEvent(false);
  };

  const handleOfflineSubmit = () => {
    let check = "";
    if (!isAdmin) {
      check = window.confirm(
        "Are you sure everything looks right? You will NOT be able to submit again, only an admin can make further changes after submission."
      );
    } else {
      check = true;
    }
    if (check) {
      setShowButtons(false);
      NotificationManager.warning(
        "You are currently operating offline. Your changes have been cached for now, and you will be able to sync once you are online again.",
        "Warning",
        10000
      );
    }
  };

  const fetchEvent = useCallback(() => {
    return api.get(`/events/${params.eventId}`).then((r) => {
      storageManager.setItem(`event/${params.eventId}/originalEvent`, JSON.stringify(r.data));
      return r;
    });
  }, [api, params.eventId]);

  const getEvent = useCallback(() => {
    fetchEvent().then((r) => {
      setEvent(r.data);
      setTempTaxPercent(r.data.tax_rate * 100);
      setTempCCPercent(r.data.cc_rate * 100);
    });
  }, [setEvent, setTempTaxPercent, setTempCCPercent, fetchEvent]);

  const getEventFees = useCallback(() => {
    api.get(`/event_fees?event_id=${params.eventId}`).then((r) => {
      setEventFees(r.data);
      storageManager.setItem(`event/${params.eventId}/originalFees`, JSON.stringify(r.data));
    });
  }, [api, setEventFees, params.eventId]);

  const getEventCategorySetups = useCallback(() => {
    api.get(`/event_category_setups?event_id=${params.eventId}`).then((r) => {
      storageManager.setItem(
        `event/${params.eventId}/originalClothingSetup`,
        JSON.stringify(r.data.find((one) => one.name === "Clothing"))
      );
      storageManager.setItem(
        `event/${params.eventId}/originalOtherSetup`,
        JSON.stringify(r.data.find((one) => one.name === "Other"))
      );
      storageManager.setItem(
        `event/${params.eventId}/originalMusicSetup`,
        JSON.stringify(r.data.find((one) => one.name === "Music"))
      );

      const clothingSetup = r.data.find((one) => one.name === "Clothing");
      const otherSetup = r.data.find((one) => one.name === "Other");
      const musicSetup = r.data.find((one) => one.name === "Music");

      setClothingSetup({
        ...clothingSetup,
        temp_decimal: clothingSetup.hall_rate * 100,
        tax_rate_override_pct: clothingSetup.tax_rate_override * 100,
      });
      setOtherSetup({
        ...otherSetup,
        temp_decimal: otherSetup.hall_rate * 100,
        tax_rate_override_pct: otherSetup.tax_rate_override * 100,
      });
      setMusicSetup({
        ...musicSetup,
        temp_decimal: musicSetup.hall_rate * 100,
        tax_rate_override_pct: musicSetup.tax_rate_override !== null ? musicSetup.tax_rate_override * 100 : undefined,
      });
    });
  }, [api, params.eventId, setClothingSetup, setOtherSetup, setMusicSetup]);

  const getSales = useCallback(() => {
    api.get(`/sales/all?artist_id=${filterData.artist_id}&event_id=${params.eventId}`).then((r) => {
      setSales(r.data);
    });
  }, [filterData, api, params.eventId]);

  useEffect(() => {
    fetchEvent().then((r) => {
      console.log("EventSummary", "fetchEvent", r);
      const localInfo = storageManager.getItem(`event/${params.eventId}/info`);
      if (!localInfo) {
        console.log("EventSummary", "No local info found, use server version");
        setEvent(r.data);
        setTempTaxPercent(r.data.tax_rate * 100);
        setTempCCPercent(r.data.cc_rate * 100);
        return;
      }
      const localEvent = JSON.parse(localInfo);
      if (r.data.updated_at > localEvent.updated_at) {
        console.log("EventSummary", "Server version is newer");
        setShowNewerVersionWarning(true);
        return;
      }
      console.log("EventSummary", "Server version is not newer");
    });
  }, [getEvent, params.eventId, setEvent, fetchEvent, setTempTaxPercent, setTempCCPercent]);

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/clothingSetup`) && getEventCategorySetups();
  }, [getEventCategorySetups, params.eventId]);

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/fees`) && getEventFees();
  }, [getEventFees, params.eventId]);

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/tempTaxPercent`) && setTempTaxPercent(event.tax_rate * 100);
  }, [event.tax_rate, params.eventId]); //eslint-disable-line

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/tempCCPercent`) && setTempCCPercent(event.cc_rate * 100);
  }, [event.cc_rate, params.eventId]); //eslint-disable-line

  useEffect(() => {
    getSales();
  }, [getSales]);

  useEffect(() => {
    event.versions?.length > 0 &&
      api.get(`/users/${event.versions[0].whodunnit}`).then((r) => {
        setEventLastUpdatedBy(r.data);
      });
  }, [api, event.versions]);

  useEffect(() => {
    api.get(`/artists/${event.artist_id}`).then((r) => {
      setArtist(r.data);
    });
  }, [api, event]);

  const salesCopy = [...sales];

  salesCopy.forEach((sale) => {
    const diff =
      sale.count_in +
      sale.add +
      sale.add_2 +
      sale.add_3 +
      sale.add_4 -
      sale.count_out -
      sale.damage_quantity -
      sale.discount_quantity -
      sale.band_comp_quantity -
      sale.ctm_comp -
      sale.management_comp;
    sale.total_sales = diff * sale.price;
  });

  const total_sales = salesCopy.reduce((total, product) => {
    const productCategoryName = product.category?.name;
    const category = {
      Clothing: clothingSetup,
      Other: otherSetup,
      Music: musicSetup,
    }[productCategoryName];
    const sales = product.total_sales;
    const taxRate = category?.tax_rate_override ? parseFloat(category.tax_rate_override) : tempTaxPercent / 100;
    const shouldTax = category?.taxed && event.new_tax_formula;
    const salesPostTax = shouldTax ? sales * (1 + taxRate) : sales;
    return total + salesPostTax;
  }, 0);

  const event_fees_total = eventFees
    .filter((each) => each.toRemove !== true)
    .reduce((a, b) => {
      return a + parseFloat(b.amount);
    }, 0);

  const before_hall_event_fees_total = eventFees
    .filter((each) => each.toRemove !== true)
    .filter((each) => each.before_hall_fee)
    .reduce((a, b) => {
      return a + parseFloat(b.amount);
    }, 0);

  eventFees.forEach((each) => {
    if (each.before_hall_fee) {
      each.Clothing = each.amount;
      each.Other = 0;
      each.Music = 0;
    } else {
      each.Clothing = each.amount * (clothingSetup.gross_sales / total_sales);
      each.Other = each.amount * (otherSetup.gross_sales / total_sales);
      each.Music = each.amount * (musicSetup.gross_sales / total_sales);
    }
  });

  const [year, month, day] = event.date.split("T")[0].split("-");
  event.formattedDate = `${month}/${day}/${year}`;

  const renderCategoryHallRates = (each, offlineState) => {
    let CCSales = event.cc_sales;
    if (typeof event.cc_sales === "string" && event.cc_sales !== "") {
      CCSales = parseFloat(event.cc_sales?.replace(/,/g, ""));
    } else if (event.cc_sales === "") {
      CCSales = 0;
    }
    if (each) {
      if (!event.new_tax_formula) {
        each.gross_sales = salesCopy
          .filter((sale) => sale.product_category === each.name)
          .reduce((a, b) => {
            return a + b.total_sales;
          }, 0);
      } else {
        each.gross_sales = salesCopy
          .filter((sale) => sale.product_category === each.name)
          .reduce((a, b) => {
            return a + b.total_sales;
          }, 0);
        if (each.taxed) {
          const taxRate = each.tax_rate_override ? parseFloat(each.tax_rate_override) : tempTaxPercent / 100;
          each.gross_sales *= 1 + taxRate;
        }
      }
      if (each.taxed) {
        const taxRate = each.tax_rate_override ? parseFloat(each.tax_rate_override) : tempTaxPercent / 100;
        each.gross_tax = +(each.gross_sales - each.gross_sales / (1 + taxRate))?.toFixed(2);
      } else {
        each.gross_tax = 0;
      }
      if (total_sales) {
        if (!event.cc_fee_prorated) {
          if (each.name === "Clothing") {
            each.cc_fee = (CCSales * tempCCPercent) / 100;
          } else {
            each.cc_fee = 0;
          }
        } else if (each.name === "Clothing") {
          each.cc_fee = (CCSales * tempCCPercent) / 100 - musicSetup.cc_fee - otherSetup.cc_fee;
        } else {
          each.cc_fee = +(
            ((salesCopy
              .filter((sale) => sale.product_category === each.name)
              .reduce((a, b) => {
                return a + b.total_sales;
              }, 0) /
              total_sales) *
              CCSales *
              tempCCPercent) /
            100
          )?.toFixed(2);
        }
      } else {
        each.cc_fee = 0;
      }

      each.event_fee = eventFees
        .filter((each) => each.before_hall_fee)
        .reduce((a, b) => {
          return a + parseFloat(b[each.name]);
        }, 0);
      if (event.cc_before_net) {
        each.gross_hall = +(
          (each.gross_sales -
            each.gross_tax -
            each.cc_fee -
            eventFees
              .filter((fee) => fee.before_hall_fee)
              .reduce((a, b) => {
                return a + parseFloat(b[each.name]);
              }, 0)) *
          each.hall_rate
        )?.toFixed(2);
      } else {
        each.gross_hall = +(
          (each.gross_sales -
            each.gross_tax -
            eventFees
              .filter((fee) => fee.before_hall_fee)
              .reduce((a, b) => {
                return a + parseFloat(b[each.name]);
              }, 0)) *
          each.hall_rate
        )?.toFixed(2);
      }
      if (each.taxed) {
        each.taxable = each.gross_sales;
      } else {
        each.taxable = 0;
      }
    }
    return (
      <>
        {!hideButtons ? (
          <div className="flex justify-between my-2">
            <div className="w-1/8 pr-2">
              <Input
                name={`${each?.name}_name`}
                value={each?.name ?? ""}
                onChange={(e) => handleChange(e, offlineState)}
                disabled
              />
            </div>
            <div className="w-1/8 pr-2">
              <CleaveInput
                name={`${each?.name}_hall_rate`}
                value={each?.temp_decimal ?? ""}
                onChange={(e) => handleHallRateChange(each?.name, e, offlineState)}
                cleaveOptions={{
                  numeral: true,
                  numeralPositiveOnly: true,
                  numeralDecimalScale: 3,
                }}
                inputClassName="w-full"
                postfix={"%"}
              />
              <div className="flex justify-end -mt-6 mr-2 text-black">%</div>
            </div>
            <div className="w-1/8 pr-2">
              <Input
                name={`${each?.name}_tax_rate_override`}
                value={each?.tax_rate_override_pct || ""}
                onChange={(e) => handleTaxRateOverrideChange(each?.name, e, offlineState)}
                placeholder={tempTaxPercent}
                cleaveOptions={{
                  numeral: true,
                  numeralPositiveOnly: true,
                  numeralDecimalScale: 3,
                }}
                inputClassName="w-full"
                postfix={"%"}
              />
              <div className="flex justify-end -mt-6 mr-2 text-black">%</div>
            </div>
            <div className="w-1/8 pr-2">
              <Input
                name={`${each?.name}_gross_sales`}
                value={
                  each?.gross_sales
                    ? each?.gross_sales?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })
                    : ""
                }
                onChange={(e) => handleChange(e, offlineState)}
                paddingLeft
                disabled
              />
              <div className="flex -mt-6 ml-2 text-black">$</div>
            </div>
            <div className="w-1/8 pr-2">
              <Input
                name={`${each?.name}_gross_tax`}
                value={
                  each?.gross_tax
                    ? each.gross_tax?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })
                    : ""
                }
                onChange={(e) => handleChange(e, offlineState)}
                paddingLeft
                disabled
              />
              <div className="flex -mt-6 ml-2 text-black">$</div>
            </div>
            <div className="w-1/8 pr-2">
              <Input
                name={`${each?.name}_cc_fee`}
                value={
                  each?.cc_fee
                    ? each?.cc_fee?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })
                    : ""
                }
                onChange={(e) => handleChange(e, offlineState)}
                paddingLeft
                disabled
              />
              <div className="flex -mt-6 ml-2 text-black">$</div>
            </div>
            <div className="w-1/8 pr-2">
              <Input
                name={`${each?.name}_event_fee`}
                value={
                  each?.event_fee
                    ? each?.event_fee?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })
                    : ""
                }
                onChange={(e) => handleChange(e, offlineState)}
                paddingLeft
                disabled
              />
              <div className="flex -mt-6 ml-2 text-black">$</div>
            </div>
            <div className="w-1/8 pr-2">
              <Input
                name={`${each?.name}_gross_hall`}
                value={
                  each?.gross_hall
                    ? each?.gross_hall?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })
                    : ""
                }
                onChange={(e) => handleChange(e, offlineState)}
                paddingLeft
                disabled
              />
              <div className="flex -mt-6 ml-2 text-black">$</div>
            </div>
            <div className="w-1/12 flex justify-center self-center">
              <input
                type="checkbox"
                id={`${each?.name}_taxed`}
                checked={!!each?.taxed}
                onChange={() => {
                  handleTaxedChange(each?.name, !each?.taxed, offlineState);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex text-right justify-between my-2">
            <div className="text-left w-1/8">{each?.name ?? ""}</div>
            <div className="w-1/8">
              {(each?.temp_decimal / 100)?.toLocaleString("en-US", {
                style: "percent",
                maximumFractionDigits: 3,
                minimumFractionDigits: 3,
              }) ?? ""}
            </div>
            <div className="w-1/8">
              {parseFloat(each?.tax_rate_override ? each.tax_rate_override : tempTaxPercent / 100)?.toLocaleString(
                "en-US",
                {
                  style: "percent",
                  maximumFractionDigits: 3,
                  minimumFractionDigits: 3,
                }
              )}
            </div>
            <div className="w-1/8">
              {each?.gross_sales
                ? each?.gross_sales?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : 0}
            </div>
            <div className="w-1/8">
              {each?.gross_tax
                ? each.gross_tax?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : 0}
            </div>
            <div className="w-1/8">
              {each?.cc_fee
                ? each?.cc_fee?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : 0}
            </div>
            <div className="w-1/8">
              {each?.event_fee
                ? each?.event_fee?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : 0}
            </div>
            <div className="w-1/8">
              {each?.gross_hall
                ? each?.gross_hall?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : 0}
            </div>
            <div className="w-1/12 flex justify-center self-center">
              <div>{each?.taxed && <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>}</div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderEventFees = (each, i, offlineState) => {
    return (
      <>
        {!hideButtons ? (
          <div className="flex justify-between mt-1">
            <div className="w-1/2">
              <Input
                name="description"
                value={each?.description ?? ""}
                onChange={(e) => handleFeeChange(e, i, offlineState)}
                required
              />
            </div>
            <div className="w-1/6">
              <CleaveInput
                name="amount"
                value={each?.amount ?? ""}
                onChange={(e) => handleFeeChange(e, i, offlineState)}
                required
                cleaveOptions={{
                  numeral: true,
                }}
                inputClassName="w-full"
                prefix={"$"}
                numericResult
              />
            </div>
            <div className="w-1/8 text-center self-center">
              <input
                type="checkbox"
                id="before_hall_fee"
                checked={!!each?.before_hall_fee}
                onChange={() => handleFeeCheckChange(!each?.before_hall_fee, i, offlineState)}
              />
            </div>
            <div className="w-1/6">
              <Button
                className="button button-white"
                id={`remove_fee_${i}`}
                name={i}
                onClick={(e) => removeFee(e, each, i, offlineState)}
                text="Remove"
                disabled={isFetching}
              ></Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between mt-1">
            <div className="w-1/2">{each?.description ?? ""}</div>
            <div className="w-1/6">${each?.amount ?? ""}</div>
            <div className="w-1/8 text-center self-center">
              <div>{each?.before_hall_fee && <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>}</div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderForm = (offlineState) => {
    return (
      <>
        {!hideButtons ? (
          <form className="text-xs">
            <div className="text-xl font-medium text-black">Event Info</div>
            <hr className="mt-1 mb-4"></hr>
            <div className="flex justify-between mb-4">
              <div className="w-9/20">
                <Input
                  lightFont
                  name="location"
                  label="Location"
                  value={`${event.city}, ${event.state}` ?? ""}
                  onChange={(e) => handleChange(e, offlineState)}
                  required
                  disabled
                />
              </div>
              <div className="w-9/20">
                <Input
                  lightFont
                  name="attendance"
                  label="Attendance"
                  value={event.attendance ?? ""}
                  onChange={(e) => handleChange(e, offlineState)}
                />
              </div>
            </div>
            <div className="flex justify-between">
              <div className="w-9/20">
                <CleaveInput
                  lightFont
                  name="tempTaxPercent"
                  label="Tax rate"
                  value={tempTaxPercent ?? ""}
                  onChange={(e) => handleTempTaxPercent(e, offlineState)}
                  cleaveOptions={{
                    numeral: true,
                    numeralPositiveOnly: true,
                    numeralDecimalScale: 3,
                  }}
                  inputClassName="w-full"
                  postfix="%"
                />
              </div>
              <div className="w-9/20">
                <Input
                  lightFont
                  name="per_cap"
                  label="Per cap sales"
                  value={event.attendance ? (total_sales / event.attendance)?.toFixed(2) : ""}
                  onChange={(e) => handleChange(e, offlineState)}
                  required
                  disabled
                  paddingLeft
                />
                <div className="flex -mt-6 ml-2 text-black">$</div>
              </div>
            </div>
            <div className="mt-12 tracking-normal font-medium text-lg text-black">CC Info</div>
            <hr className="mt-1 mb-4"></hr>
            <div className="flex items-end">
              <div className="w-1/5 mr-6 mt-2">
                <CleaveInput
                  lightFont
                  name="cc_sales"
                  label="Credit card sales"
                  value={event.cc_sales ?? ""}
                  onChange={(e) => handleChange(e, offlineState)}
                  cleaveOptions={{
                    numeral: true,
                    numeralPositiveOnly: true,
                    numeralDecimalScale: 2,
                  }}
                  inputClassName="w-full"
                  prefix={"$"}
                />
              </div>
              <div className="w-1/5 mr-6 mb-2">
                <CleaveInput
                  lightFont
                  name="tempCCPercent"
                  label="Rate"
                  value={tempCCPercent ?? ""}
                  onChange={(e) => handleTempCCPercent(e, offlineState)}
                  cleaveOptions={{
                    numeral: true,
                    numeralPositiveOnly: true,
                    numeralDecimalScale: 3,
                  }}
                  inputClassName="w-full"
                  postfix="%"
                />
                <div className="flex justify-end -mt-6 mr-2 text-black">%</div>
              </div>
              <div className="w-1/5 mb-2">
                <label className="flex justify-center text-center" htmlFor="cc_before_net">
                  CC Before Net
                </label>
                <div className="flex justify-center">
                  <input
                    className="mt-5"
                    type="checkbox"
                    id="cc_before_net"
                    checked={!!event.cc_before_net}
                    onChange={(e) => handleCheckChange(e, !event.cc_before_net, offlineState)}
                  />
                </div>
              </div>
              <div className="w-1/5 flex flex-col mb-2">
                <label className="text-center mb-2" htmlFor="cc_fee_prorated">
                  CC Prorated
                </label>
                <div className="text-center -mb-2 mt-1.5">
                  <Switch
                    onChange={() => {
                      handleCCProrateToggle(event.cc_fee_prorated, offlineState);
                    }}
                    checked={!!event.cc_fee_prorated}
                    height={22}
                    width={40}
                    onColor="#3b82f6"
                    offColor="#d4d4d8"
                    id="cc_fee_prorated"
                  />
                </div>
              </div>
            </div>
            <div className="mt-12 tracking-normal font-medium text-lg text-black">Category Fees</div>
            <hr className="mt-1 mb-4"></hr>
            <div className="w-full flex justify-between text-black items-end">
              <div className="w-1/8 pr-2">Category</div>
              <div className="w-1/8 pr-2">Hall rate</div>
              <div className="w-1/8 pr-2">Tax rate</div>
              <div className="w-1/8 pr-2">Gross sales</div>
              <div className="w-1/8 pr-2">Tax collected</div>
              <div className="w-1/8 pr-2">CC fee</div>
              <div className="w-1/8 pr-2">Event fee</div>
              <div className="w-1/8 pr-2">Hall fee</div>
              <div className="w-1/12 flex justify-center">Taxed</div>
            </div>
            {clothingSetup && renderCategoryHallRates(clothingSetup, offlineState)}
            {otherSetup && renderCategoryHallRates(otherSetup, offlineState)}
            {musicSetup && renderCategoryHallRates(musicSetup, offlineState)}
            <div className="mt-12 tracking-normal font-medium text-lg text-black">Tax</div>
            <hr className="mt-1 mb-4"></hr>
            <div>
              <input
                checked={event.tax_kept_by_venue}
                onChange={(e) => handleRadioChange(e, offlineState)}
                type="radio"
                id="tax_kept_by_venue_true"
                name="tax_kept_by_venue"
                value={true}
              />
              <label htmlFor="tax_kept_by_venue_true" className="text-sm pl-2 pr-8">
                Venue Responsible
              </label>
            </div>
            <div className="flex justify-between w-3/4 my-4 text-sm">
              <div>
                <input
                  checked={!event.tax_kept_by_venue}
                  onChange={(e) => handleRadioChange(e, offlineState)}
                  type="radio"
                  id="tax_kept_by_venue_false"
                  name="tax_kept_by_venue"
                  value=""
                />
                <label htmlFor="tax_kept_by_venue_false" className="pl-2">
                  Artist Responsible
                </label>
              </div>
              {!event.tax_kept_by_venue && (
                <>
                  <AdminDiv>
                    <div>
                      <input
                        checked={event.remitted}
                        onChange={(e) => handleRadioChange(e, offlineState)}
                        type="radio"
                        id="remitted_true"
                        name="remitted"
                        value={true}
                      />
                      <label htmlFor="remitted_true" className="pl-2">
                        Remit Payment
                      </label>
                    </div>
                  </AdminDiv>
                  <AdminDiv>
                    <div>
                      <input
                        checked={!event.remitted}
                        onChange={(e) => handleRadioChange(e, offlineState)}
                        type="radio"
                        id="remitted_false"
                        name="remitted"
                        value=""
                      />
                      <label htmlFor="remitted_false" className="pl-2">
                        Does Not Remit
                      </label>
                    </div>
                  </AdminDiv>
                </>
              )}
            </div>
            <div className="text-center flex">
              <Switch
                onChange={() => {
                  handleNewTaxFormulaToggle(event.new_tax_formula, offlineState);
                }}
                checked={!!event.new_tax_formula}
                height={22}
                width={40}
                onColor="#3b82f6"
                offColor="#d4d4d8"
                id="new_tax_formula"
              />
              <label htmlFor="new_tax_formula" className="text-sm ml-4">
                Tax Exclusive
              </label>
            </div>
            <div className="mt-12 tracking-normal font-medium text-lg text-black">Payment</div>
            <hr className="mt-1 mb-4"></hr>
            <div className="flex">
              <div>
                <div>
                  <input
                    type="radio"
                    name="vendor"
                    id="venue_sold"
                    value="Venue"
                    checked={event.vendor === "Venue"}
                    onChange={(e) => handlePartySoldChange(e, offlineState)}
                  />
                  <label className="ml-2 text-sm" htmlFor="venue_sold">
                    Venue Sold
                  </label>
                  <div className="ml-5 mt-4 flex">
                    <input
                      className="-mt-2"
                      type="checkbox"
                      id="cash_received"
                      checked={!!event.cash_received}
                      onChange={(e) => handleCheckChange(e, !event.cash_received, offlineState)}
                      disabled={event.vendor !== "Venue"}
                    />
                    <div className="flex justify-between">
                      <label className="ml-2 text-sm" htmlFor="cash_received">
                        Cash Paid to Artist
                      </label>
                      <CleaveInput
                        name="cash_paid_to_artist"
                        value={event.cash_paid_to_artist ?? ""}
                        className="h-9 ml-3 -mt-2 rounded-md border w-1/3"
                        onChange={(e) => handleChange(e, offlineState)}
                        disabled={!event.cash_received}
                        placeholder={event.total_from_venue}
                        cleaveOptions={{
                          numeral: true,
                          numeralPositiveOnly: true,
                        }}
                        inputClassName="w-full"
                        prefix={"$"}
                      />
                    </div>
                  </div>
                  <div className="ml-5 mt-12 flex">
                    <input
                      className="-mt-2"
                      type="checkbox"
                      id="check_due"
                      checked={!!event.check_due}
                      onChange={(e) => handleCheckChange(e, !event.check_due, offlineState)}
                      disabled={event.vendor !== "Venue"}
                    />
                    <div className="flex justify-between">
                      <label className="ml-2 text-sm" htmlFor="check_due">
                        Check Due to Artist
                      </label>
                      <CleaveInput
                        name="check_due_to_artist"
                        value={event.check_due_to_artist ?? ""}
                        className="h-9 ml-2 -mt-2 rounded-md border w-1/3"
                        onChange={(e) => handleChange(e, offlineState)}
                        disabled={!event.check_due}
                        placeholder={event.total_from_venue}
                        cleaveOptions={{
                          numeral: true,
                          numeralPositiveOnly: true,
                        }}
                        inputClassName="w-full"
                        prefix={"$"}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <input
                    type="radio"
                    name="vendor"
                    id="artist_sold"
                    value="Artist"
                    checked={event.vendor === "Artist"}
                    onChange={(e) => handlePartySoldChange(e, offlineState)}
                  />
                  <label className="ml-2 text-sm" htmlFor="artist_sold">
                    Artist Sold
                  </label>
                  <div className="ml-5 mt-4 flex">
                    <input
                      className="-mt-2"
                      type="checkbox"
                      id="cash_received_venue"
                      checked={!!event.cash_received_venue}
                      onChange={(e) => handleCheckChange(e, !event.cash_received_venue, offlineState)}
                      disabled={event.vendor !== "Artist" && !event.cash_paid_to_venue}
                    />
                    <div className="flex justify-between">
                      <label className="ml-2 text-sm" htmlFor="cash_received_venue">
                        Cash Paid to Venue
                      </label>
                      <CleaveInput
                        name="cash_paid_to_venue"
                        value={event.cash_paid_to_venue ?? ""}
                        className="h-9 ml-2 -mt-2 rounded-md border w-1/3"
                        onChange={(e) => handleChange(e, offlineState)}
                        disabled={!event.cash_received_venue}
                        placeholder={event.total_to_venue}
                        cleaveOptions={{
                          numeral: true,
                          numeralPositiveOnly: true,
                        }}
                        inputClassName="w-full"
                        prefix={"$"}
                      />
                    </div>
                  </div>
                  <div className="ml-5 mt-12 flex">
                    <input
                      className="-mt-2"
                      type="checkbox"
                      id="check_due_venue"
                      value={!!event.check_due_venue}
                      checked={!!event.check_due_venue}
                      onChange={(e) => handleCheckChange(e, !event.check_due_venue, offlineState)}
                      disabled={event.vendor !== "Artist" && !event.check_due_to_venue}
                    />
                    <div className="flex justify-between">
                      <label className="ml-2 text-sm" htmlFor="check_due_venue">
                        Check Due to Venue
                      </label>
                      <CleaveInput
                        name="check_due_to_venue"
                        value={event.check_due_to_venue ?? ""}
                        className="h-9 -mt-2 rounded-md border w-1/3"
                        onChange={(e) => handleChange(e, offlineState)}
                        disabled={!event.check_due_venue}
                        placeholder={event.total_to_venue}
                        cleaveOptions={{
                          numeral: true,
                          numeralPositiveOnly: true,
                        }}
                        inputClassName="w-full"
                        prefix={"$"}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 tracking-normal font-medium text-lg text-black">Event Fees</div>
            <hr className="mt-1 mb-4"></hr>
            <div className="flex justify-between text-black items-end mb-2">
              <div className="w-1/2">Fee Description</div>
              <div className="w-1/6">Amount</div>
              <div className="w-1/8 text-center">Before Hall Fee</div>
              <div className="w-1/6"></div>
            </div>
            {eventFees.map((each, i) => (
              <div key={i} name="event_fee" id={`remove_fee_${i}`}>
                {each.toAdd !== false && renderEventFees(each, i, offlineState)}
              </div>
            ))}
            {!hideButtons && (
              <div className="my-4">
                <Button
                  text="Add New Fee"
                  disabled={isFetching}
                  className="button mr-2"
                  onClick={(e) => addFee(e, offlineState)}
                ></Button>
              </div>
            )}
            <div id="artist_info" className="text-sm">
              <div id="created_by" className="text-sm">
                <div className="mt-12 tracking-normal font-medium text-lg text-black">Event Created By</div>
                <hr className="mt-1 mb-4"></hr>
                <div className="flex py-2">
                  <div className="w-1/3">Name</div>
                  <div className="w-1/2">
                    {eventLastUpdatedBy.first_name} {eventLastUpdatedBy.last_name}
                  </div>
                </div>
                <div className="flex py-2">
                  <div className="w-1/3">Email</div>
                  <div className="w-1/2">{eventLastUpdatedBy.email}</div>
                </div>
                <div className="flex py-2">
                  <div className="w-1/3">Date</div>
                  {event.versions?.length > 0 && (
                    <div className="w-1/2">{event.versions[0]?.created_at?.split("T")[0]}</div>
                  )}
                </div>
              </div>
              <div className="mt-6 tracking-normal font-medium text-lg text-black">Artist Payment Info</div>
              <hr className="mt-1 mb-4"></hr>
              <div className="flex py-2">
                <div className="w-1/3">Business Name</div>
                <div className="w-1/2">{artist.business_name}</div>
              </div>
              <div className="flex py-2">
                <div className="w-1/3">Address</div>
                <div className="w-1/2">{artist.street_address}</div>
              </div>
              {artist.city && (
                <div className="flex py-2">
                  <div className="w-1/3"></div>
                  <div className="w-1/2">
                    {artist.city}, {artist.state} {artist.zip_code}
                  </div>
                </div>
              )}
              <div className="flex py-2">
                <div className="w-1/3">Fed Tax #</div>
                <div className="w-1/2">{artist.tax_id}</div>
              </div>
            </div>
          </form>
        ) : (
          <form className="text-sm">
            <div className="text-xl font-medium mb-6">
              {filterData.artist_name} - {event.name} - {event.formattedDate}
            </div>
            <div className="text-xl font-medium">Event Info</div>
            <hr className="mb-2"></hr>
            <div className="flex justify-between mb-4">
              <div className="w-9/20">
                <label className="font-semibold">Location</label>
                <div className="mt-4">{`${event.city}, ${event.state}` ?? ""}</div>
              </div>
              <div className="w-9/20">
                <label className="font-semibold">Attendance</label>
                <div className="mt-4">{`${event.attendance}` ?? ""}</div>
              </div>
              <div className="w-9/20">
                <label className="font-semibold">Tax rate</label>
                <div className="mt-4">
                  {`${(tempTaxPercent / 100)?.toLocaleString("en-US", {
                    style: "percent",
                    maximumFractionDigits: 3,
                    minimumFractionDigits: 3,
                  })}` ?? ""}
                </div>
              </div>
              <div className="w-9/20">
                <label className="font-semibold">Per cap sales</label>
                <div className="mt-4">${event.attendance ? (total_sales / event.attendance)?.toFixed(2) : ""}</div>
              </div>
            </div>
            <div className="mt-6 tracking-normal font-medium text-lg">Category Fees</div>
            <hr className="mb-2"></hr>
            <div className="flex text-right justify-between items-end font-semibold">
              <div className="text-left w-1/8">Category</div>
              <div className="w-1/8">Hall rate</div>
              <div className="w-1/8">Tax rate</div>
              <div className="w-1/8">Gross sales</div>
              <div className="w-1/8">Tax collected</div>
              <div className="w-1/8">CC fee</div>
              <div className="w-1/8">Event fee</div>
              <div className="w-1/8">Hall fee</div>
              <div className="w-1/12 flex justify-center">Taxed</div>
            </div>
            {clothingSetup && renderCategoryHallRates(clothingSetup, offlineState)}
            {otherSetup && renderCategoryHallRates(otherSetup, offlineState)}
            {musicSetup && renderCategoryHallRates(musicSetup, offlineState)}
            <div className="flex w-8/5 mt-16">
              <div className="w-2/3">
                <div className="tracking-normal font-medium text-lg">Event Fees</div>
                <hr className="mb-2"></hr>
                <div className="flex justify-between items-end mb-2 font-semibold">
                  <div className="w-1/2">Fee Description</div>
                  <div className="w-1/6">Amount</div>
                  <div className="w-1/8 text-center">Before Hall Fee</div>
                </div>
                {eventFees.map((each, i) => (
                  <div key={i} name="event_fee" id={`remove_fee_${i}`}>
                    {each.toAdd !== false && renderEventFees(each, i, offlineState)}
                  </div>
                ))}
              </div>
              <div className="w-1/3 ml-12">
                <div className="tracking-normal font-medium text-lg">CC Info</div>
                <hr className="mb-2"></hr>
                <div className="flex items-end">
                  <div className="w-1/3 mr-6">
                    <label className="font-semibold">Credit card sales</label>
                    <div className="mt-4">${event.cc_sales ? event.cc_sales : 0}</div>
                  </div>
                  <div className="w-1/3">
                    <label className="font-semibold">Rate</label>
                    <div className="mt-4">{`${tempCCPercent}` ?? ""}%</div>
                  </div>
                  <div className="w-1/3">
                    <label className="font-semibold">CC Before Net</label>
                    <div className="mt-4">
                      {event?.cc_before_net ? (
                        <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>
                      ) : (
                        <div className="h-5"></div>
                      )}
                    </div>
                  </div>
                  <div className="w-1/3">
                    <label className="font-semibold">CC Prorated</label>
                    <div className="mt-4">
                      {event?.cc_fee_prorated ? (
                        <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>
                      ) : (
                        <div className="h-5"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex w-8/5 mt-6">
              <div id="artist_info" className="text-sm w-2/3">
                <div className="tracking-normal font-medium text-lg">Artist Payment Info</div>
                <hr className="mb-2"></hr>
                <div className="flex py-2">
                  <div className="w-1/3 font-semibold">Business Name</div>
                  <div className="w-1/2">{artist.business_name}</div>
                </div>
                <div className="flex py-2">
                  <div className="w-1/3 font-semibold">Address</div>
                  <div className="w-1/2">{artist.street_address}</div>
                </div>
                {artist.city && (
                  <div className="flex py-2">
                    <div className="w-1/3 font-semibold"></div>
                    <div className="w-1/2">
                      {artist.city}, {artist.state} {artist.zip_code}
                    </div>
                  </div>
                )}
                <div className="flex py-2">
                  <div className="w-1/3 font-semibold">Fed Tax #</div>
                  <div className="w-1/2">{artist.tax_id}</div>
                </div>
              </div>
              <div className="w-1/3 ml-12">
                <div className="tracking-normal font-medium text-lg">Tax</div>
                <hr className="mb-2"></hr>
                {event.tax_kept_by_venue ? (
                  <label className="text-sm mb">Venue Responsible</label>
                ) : (
                  <label className="text-sm">Artist Responsible</label>
                )}
                {!event.tax_kept_by_venue && (
                  <AdminDiv className="text-sm mt-2">
                    {event.remitted ? <label>Remit Payment</label> : <label>Does Not Remit</label>}
                  </AdminDiv>
                )}
              </div>
            </div>
            <div className="flex w-8/5 mt-6">
              <div id="created_by" className="w-2/3 text-sm">
                <div className="tracking-normal font-medium text-lg">Event Created By</div>
                <hr className="mb-2"></hr>
                <div className="flex py-2">
                  <div className="w-1/3 font-semibold">Email</div>
                  <div className="w-1/2">{eventLastUpdatedBy.email}</div>
                </div>
                <div className="flex py-2">
                  <div className="w-1/3 font-semibold">Date</div>
                  {event.versions?.length > 0 && (
                    <div className="w-1/2">{event.versions[0]?.created_at?.split("T")[0]}</div>
                  )}
                </div>
              </div>
              <div className="w-1/3 ml-12">
                <div className="tracking-normal font-medium text-lg">Payment</div>
                <hr className="mb-2"></hr>
                <div className="flex text-sm">
                  <div>
                    {event.vendor === "Venue" ? (
                      <div className="py-2">
                        <label className="font-semibold">Venue Sold</label>
                        {!!event.cash_paid_to_artist && (
                          <div className="mt-4">
                            <div className="flex justify-between">
                              <label className="font-semibold">Cash Paid to Artist</label>
                              <div className="ml-9">${event.cash_paid_to_artist.toFixed(2) ?? ""}</div>
                            </div>
                          </div>
                        )}
                        {!!event.check_due_to_artist && (
                          <div className="mt-4 flex">
                            <div className="flex justify-between">
                              <label className="font-semibold">Check Due to Artist</label>
                              <div className="ml-8">${event.check_due_to_artist.toFixed(2) ?? ""}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      event.vendor === "Artist" && (
                        <div className="py-2">
                          <label className="font-semibold">Artist Sold</label>
                          {!!event.cash_paid_to_venue && (
                            <div className="mt-4">
                              <div className="flex justify-between">
                                <label className="font-semibold">Cash Paid to Venue</label>
                                <div className="ml-9">${event.cash_paid_to_venue.toFixed(2) ?? ""}</div>
                              </div>
                            </div>
                          )}
                          {!!event.check_due_to_venue && (
                            <div className="mt-4 flex">
                              <div className="flex justify-between">
                                <label className="font-semibold">Check Due to Venue</label>
                                <div className="ml-8">${event.check_due_to_venue.toFixed(2) ?? ""}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr className="w-8/5"></hr>
            <div className="flex w-8/5">
              <div className="w-2/3">
                <div className="flex mt-8">
                  <div className="w-1/3">TM Rep Signature</div>
                  {event.versions && <div className="w-1/2 border-b border-black"></div>}
                </div>
                <div className="flex mt-8">
                  <div className="w-1/3">Venue Rep Signature</div>
                  {event.versions && <div className="w-1/2 border-b border-black"></div>}
                </div>
              </div>
              <img src="/ctm_logo.png" alt="ctm_logo" className="w-1/3 pl-3 mt-6 object-contain h-auto" />
            </div>
          </form>
        )}
      </>
    );
  };

  return (
    <SalesLayout>
      <EventLayout dataCached={dataCached} setDataCached={setDataCached} afterSubmit={afterSubmit}>
        <div className="w-3/5">
          {showNewerVersionWarning && (
            <div className="flex items-center px-4 py-2 mb-4 bg-yellow-100 border-yellow-300">
              <span>
                There is a newer version of this event available. Click "update" to view the latest version. Note: you
                will lose any unsaved changes that you have made.
              </span>
              <Button className="button button-warning ml-2" text="Update" onClick={revert} />
            </div>
          )}
          <div className="flex justify-end">
            <div className="absolute -mt-36 justify-end">
              {hideButtons && (
                <>
                  <Button className="button button-white mr-2" text="Edit" onClick={() => handleHideButtons()}></Button>
                  <ReactToPrint
                    trigger={() => <Button className="button" text="Print"></Button>}
                    content={() => printRef.current}
                  />
                </>
              )}
              <div className="flex">
                {!hideButtons && !showButtons && (
                  <>
                    <Button className="button" text="Format Print" onClick={() => handleHideButtons()}></Button>
                  </>
                )}
                {showButtons && (event.number_of_updates < kNumberOfUpdatesAllowed || isAdmin || isManager) ? (
                  <div className="flex justify-center">
                    <Button
                      className="button button-white mx-2"
                      onClick={revert}
                      text="Revert"
                      disabled={isFetching}
                    ></Button>
                    {!recalculateAlert ? (
                      <>
                        <OnlineWrapper>
                          {!dataCached && (
                            <>
                              {event.number_of_updates === 0 && (
                                <Button
                                  text={isFetching ? `Loading ${"\u00A0"}` : "Save"}
                                  onClick={handleSaveEvent}
                                  disabled={isFetching}
                                  inProgress={isFetching}
                                  className="button mx-2"
                                ></Button>
                              )}
                              <Button
                                text={isFetching ? `Loading ${"\u00A0"}` : "Finalize"}
                                onClick={handleFinalizeEvent}
                                disabled={isFetching}
                                inProgress={isFetching}
                                className="button"
                              ></Button>
                            </>
                          )}
                        </OnlineWrapper>
                        <OfflineWrapper>
                          <Button
                            className="button"
                            onClick={handleOfflineSubmit}
                            text={isFetching ? `Loading ${"\u00A0"}` : "Finalize"}
                            disabled={isFetching}
                            inProgress={isFetching}
                          ></Button>
                        </OfflineWrapper>
                      </>
                    ) : (
                      <div className="text-red-500 text-sm mt-2">Recalculate to submit</div>
                    )}
                  </div>
                ) : (
                  <>
                    {showButtons && (
                      <div>
                        <span className="text-red-500 text-sm">Contact an admin to submit further changes</span>
                        <Button
                          className="button button-white mx-2"
                          onClick={revert}
                          text="Revert"
                          disabled={isFetching}
                        ></Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div ref={printRef} className={hideButtons ? "flex justify-between m-8" : "flex justify-between"}>
          <div className="w-5/8">
            <OnlineWrapper>{renderForm(false)}</OnlineWrapper>
            <OfflineWrapper>{renderForm(true)}</OfflineWrapper>
          </div>
          <SettlementCalculator
            event={event}
            setEvent={setEvent}
            total_sales={total_sales}
            event_fees_total={event_fees_total}
            before_hall_event_fees_total={before_hall_event_fees_total}
            clothingSetup={clothingSetup}
            otherSetup={otherSetup}
            musicSetup={musicSetup}
            setShowButtons={setShowButtons}
            recalculateAlert={recalculateAlert}
            setRecalculateAlert={setRecalculateAlert}
            artistPaymentMatchAlert={showAristsAlert()}
            venuePaymentMatchAlert={showVenueAlert()}
            setDataCached={setDataCached}
            tempCCPercent={tempCCPercent}
            tempTaxPercent={tempTaxPercent}
            hideButtons={hideButtons}
            changesMade={changesMade}
            eventFees={eventFees}
          ></SettlementCalculator>
        </div>
        <div className="flex justify-center my-8">
          {errorMessage?.error && <div className="text-red-500 pb-2">{errorMessage.error}</div>}
        </div>
      </EventLayout>
    </SalesLayout>
  );
};
export default EventSummary;
